import type { AllocationResult, Stats, TimingOptions, VisibilityState } from './types';

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function now(): number {
  return performance.now();
}

export function measureFixedWork(work: () => void, options: TimingOptions = {}): number[] {
  const { runs = 10, warmup = 2 } = options;
  for (let i = 0; i < warmup; i++) work();
  const samples: number[] = [];
  for (let i = 0; i < runs; i++) {
    const t0 = now();
    work();
    samples.push(now() - t0);
  }
  return samples;
}

export function computeStats(samples: number[]): Stats {
  const sorted = [...samples].sort((a, b) => a - b);
  const count = sorted.length;
  const min = sorted[0];
  const max = sorted[count - 1];
  const median = count % 2 === 0
    ? (sorted[count / 2 - 1] + sorted[count / 2]) / 2
    : sorted[Math.floor(count / 2)];
  const mean = samples.reduce((a, b) => a + b, 0) / count;
  const variance = samples.reduce((sum, v) => sum + (v - mean) ** 2, 0) / count;
  const std = Math.sqrt(variance);
  return { mean, median, min, max, std, cv: mean > 0 ? std / mean : 0, count };
}

export function safeAllocate(sizeBytes: number, timeoutMs = 5000): AllocationResult {
  const t0 = now();
  try {
    const buf = new ArrayBuffer(sizeBytes);
    new Uint8Array(buf)[0] = 1; // probe first byte only; full commit can hang.
    return { size: sizeBytes, success: true, durationMs: now() - t0 };
  } catch (err) {
    return { size: sizeBytes, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) };
  }
}

export function jitterProfile(samples: number[]) {
  const s = computeStats(samples);
  const unique = new Set(samples.map((v) => Math.round(v * 1000) / 1000)).size;
  const regularity = samples.length > 1 ? unique / samples.length : 1;

  let autocorr = 0;
  if (samples.length > 2) {
    const mean = s.mean;
    let num = 0;
    let den = 0;
    for (let i = 0; i < samples.length - 1; i++) {
      const a = samples[i] - mean;
      const b = samples[i + 1] - mean;
      num += a * b;
      den += a * a;
    }
    autocorr = den > 0 ? num / den : 0;
  }
  return { ...s, regularity, autocorr };
}

export function createVisibilityTracker() {
  if (typeof document === 'undefined') {
    return {
      getState: (): VisibilityState => ({
        hiddenAtStart: false,
        hiddenDuringRun: false,
        transitions: 0,
        hiddenTimeMs: 0,
        focusedAtStart: true,
        focusedDuringRun: true,
      }),
      destroy: () => {},
    };
  }

  const hiddenAtStart = !!document.hidden;
  const focusedAtStart = typeof document.hasFocus === 'function' ? document.hasFocus() : !hiddenAtStart;
  let hiddenDuringRun = hiddenAtStart;
  let focusedDuringRun = focusedAtStart;
  let transitions = 0;
  let hiddenTimeMs = 0;
  let lastHiddenAt: number | null = hiddenAtStart ? now() : null;

  function onChange() {
    const hidden = !!document.hidden;
    const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : !hidden;
    transitions++;
    hiddenDuringRun ||= hidden;
    focusedDuringRun &&= focused;
    const t = now();
    if (hidden && lastHiddenAt === null) lastHiddenAt = t;
    if (!hidden && lastHiddenAt !== null) {
      hiddenTimeMs += t - lastHiddenAt;
      lastHiddenAt = null;
    }
  }

  document.addEventListener('visibilitychange', onChange);
  window.addEventListener('blur', onChange);
  window.addEventListener('focus', onChange);

  return {
    getState(): VisibilityState {
      const t = now();
      const extra = lastHiddenAt !== null && document.hidden ? t - lastHiddenAt : 0;
      return {
        hiddenAtStart,
        hiddenDuringRun,
        transitions,
        hiddenTimeMs: hiddenTimeMs + extra,
        focusedAtStart,
        focusedDuringRun,
      };
    },
    destroy() {
      document.removeEventListener('visibilitychange', onChange);
      window.removeEventListener('blur', onChange);
      window.removeEventListener('focus', onChange);
    },
  };
}

export function formatBytes(n: number): string {
  if (n === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / k ** i).toFixed(2)} ${sizes[i]}`;
}

const DEVICE_UUID_KEY = 'pipa-device-uuid';
const DEVICE_LABEL_KEY = 'pipa-device-label';
const CPU_MODEL_KEY = 'pipa-cpu-model';
const SHOWN_MEMES_KEY = 'pipa-shown-memes';

function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceUuid(): string {
  if (typeof localStorage === 'undefined') return generateUuid();
  let uuid = localStorage.getItem(DEVICE_UUID_KEY);
  if (!uuid) {
    uuid = generateUuid();
    localStorage.setItem(DEVICE_UUID_KEY, uuid);
  }
  return uuid;
}

export function getSavedDeviceLabel(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(DEVICE_LABEL_KEY);
}

export function setSavedDeviceLabel(label: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(DEVICE_LABEL_KEY, label);
}

export function getSavedCpuModel(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(CPU_MODEL_KEY);
}

export function setSavedCpuModel(cpuModel: string): void {
  if (typeof localStorage === 'undefined') return;
  if (cpuModel.trim()) {
    localStorage.setItem(CPU_MODEL_KEY, cpuModel);
  } else {
    localStorage.removeItem(CPU_MODEL_KEY);
  }
}

export function getShownMemes(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SHOWN_MEMES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function markMemeShown(src: string): void {
  if (typeof localStorage === 'undefined') return;
  const shown = new Set(getShownMemes());
  shown.add(src);
  localStorage.setItem(SHOWN_MEMES_KEY, JSON.stringify([...shown]));
}

export function resetShownMemes(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(SHOWN_MEMES_KEY);
}
